import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListResponse, ISnListQueryParameters } from '@lib/api';
import { SnExternalAppliance } from '@lib/api/entities';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnExternalAppliancesAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getTypeList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnExternalAppliance>> {
    return this._httpClient.get('/api/external-appliances/types/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        ordering: response.ordering,
        results: response.results.map((type) => new SnExternalAppliance(type)),
      })),
    );
  }

  public getExternalApplianceList(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnExternalAppliance>> {
    return this._httpClient.get('/api/external-appliances/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((camera) => new SnExternalAppliance(camera)),
      })),
    );
  }

  public getExternalApplianceCameraList(
    id: string,
    params: ISnListQueryParameters,
  ): Observable<any> {
    return this._httpClient.get(`/api/external-appliances/${id}/available_cameras/`, { params });
  }

  public getExternalAppliance(id: string): Observable<SnExternalAppliance> {
    return this._httpClient
      .get(`/api/external-appliances/${id}/`)
      .pipe(map((response: any) => new SnExternalAppliance(response)));
  }

  public createExternalAppliance(data: any): Observable<SnExternalAppliance> {
    return this._httpClient
      .post('/api/external-appliances/', data)
      .pipe(map((response: any) => new SnExternalAppliance(response)));
  }

  public updateExternalAppliance(id: string, data: any): Observable<SnExternalAppliance> {
    return this._httpClient
      .put(`/api/external-appliances/${id}/`, data)
      .pipe(map((response: any) => new SnExternalAppliance(response)));
  }

  public reloadCameras(id: string): Observable<SnExternalAppliance> {
    return this._httpClient
      .post(`/api/external-appliances/${id}/update_camera_list/`, null)
      .pipe(map((response: any) => new SnExternalAppliance(response)));
  }

  public partialUpdateExternalAppliance(id: string, data: any): Observable<SnExternalAppliance> {
    return this._httpClient
      .patch(`/api/external-appliances/${id}/`, data)
      .pipe(map((response: any) => new SnExternalAppliance(response)));
  }

  public deleteExternalAppliance(id: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: t('appliances.dialogs.deleteExternalAppliance'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/external-appliances/${id}/`)),
        tap({
          next: () => this._toastrService.success(t('appliances.messages.externalApplianceDeleted')),
          error: () =>
            this._toastrService.error(t('appliances.errors.couldNotDeleteExternalAppliance')),
        }),
      );
  }

  public checkStatus(id: string): Observable<any> {
    return this._httpClient.get(`/api/external-appliances/${id}/check_status/`);
  }
}
